import { getLocalStorage, setLocalStorage } from '@src/utils/storage'
import { getLocalStorageBoolean, getLocalStorageSafe } from '@src/utils/storage/safe'
import { isPrimaryHue, type PrimaryHue } from '@src/theme/palette'
import create from 'zustand'

/**
 * 用户偏好的主题模式：
 *  - 'light'   始终浅色
 *  - 'dark'    始终深色
 *  - 'system'  跟随操作系统的 prefers-color-scheme
 */
export type Theme = 'light' | 'dark' | 'system'

/** 真正应用到 body 上的主题（'system' 已经被解析为 light/dark） */
export type ResolvedTheme = 'light' | 'dark'

/**
 * 内容区宽度预设：影响 Layout 内的 .layout-content max-width 与居中
 *  - compact   紧凑 — 沿用历史行为，content 撑满可用区，保留 12px 边距
 *  - medium    中等 — content 居中，最大 1280px
 *  - spacious  舒展 — content 居中，最大 1600px
 */
export type ContainerWidth = 'compact' | 'medium' | 'spacious'

/**
 * SEO 三件套：站点标题、关键词、描述。
 * 会被 useDocumentMeta 同步到 document.title / <meta name="keywords"> / <meta name="description">。
 */
export interface SeoConfig {
	title: string
	keywords: string
	description: string
}

export interface GlobalState {
	locale: 'zh_CN' | 'en_GB'
	loading: boolean
	theme: Theme
	resolvedTheme: ResolvedTheme
	primaryHue: PrimaryHue
	containerWidth: ContainerWidth
	themeDrawerOpen: boolean
	watermarkEnabled: boolean
	systemName: string
	copyright: string
	seo: SeoConfig
	siderWidth: number
	toogleLoading: (val: boolean) => void
	changeLocale: (val: 'zh_CN' | 'en_GB') => void
	/**
	 * Header 暗色按钮专用：基于 *已解析* 的主题取反，并把偏好也切到该值（脱离 system）。
	 * 走 view-transition 涟漪动画。
	 */
	switchMode: (clickX?: number, clickY?: number) => void
	/**
	 * 抽屉里 3 选 1 radio 专用：直接设置偏好为 t，可选传入点击坐标做涟漪。
	 * 如果新值是 'system'，会安装 matchMedia 监听，OS 切色时自动跟随。
	 */
	setTheme: (t: Theme, opts?: { x?: number; y?: number }) => void
	setPrimaryHue: (h: PrimaryHue) => void
	setContainerWidth: (w: ContainerWidth) => void
	setThemeDrawerOpen: (open: boolean) => void
	setWatermarkEnabled: (enabled: boolean) => void
	setSystemName: (name: string) => void
	setCopyright: (text: string) => void
	setSeo: (next: SeoConfig) => void
	setSiderWidth: (w: number) => void
}

const isLocale = (v: unknown): v is 'zh_CN' | 'en_GB' => v === 'zh_CN' || v === 'en_GB'
const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark' || v === 'system'
const isContainerWidth = (v: unknown): v is ContainerWidth => v === 'compact' || v === 'medium' || v === 'spacious'

/** 默认 SEO 三件套（中文版）。不同语言版本可以在切换 locale 时再覆盖。 */
const DEFAULT_SEO: SeoConfig = {
	title: 'Plus-One UI',
	keywords: 'Plus-One UI, admin, dashboard, react',
	description: 'Plus-One UI — A modern admin dashboard built with React + Semi Design.'
}

/** 侧边栏宽度上下限（px）。160 太窄放不下 logo+文字，320 太宽挤占主内容区。 */
const SIDER_WIDTH_MIN = 160
const SIDER_WIDTH_MAX = 320
/** 默认侧边栏宽度（px），与 Sider 组件中 Nav 的 `maxWidth: 200` 保持一致。 */
const DEFAULT_SIDER_WIDTH = 200
/** 防抖步长，用户调整时自动 round 到 4 的倍数，避免任意奇数值。 */
const SIDER_WIDTH_STEP = 4

/**
 * 从 localStorage 安全读取字符串字段，损坏/缺失/抛错都退回 fallback。
 * 与 getLocalStorageSafe 的差别是这里只接受 string（其它类型直接退回）。
 */
const getLocalStorageString = (key: string, fallback: string): string => {
	try {
		const raw = getLocalStorage(key)
		return typeof raw === 'string' && raw.length > 0 ? raw : fallback
	} catch {
		return fallback
	}
}

/**
 * 从 localStorage 安全读取 JSON 对象，解析失败 / 类型不符 / 抛错都退回 fallback。
 * 用在 SeoConfig 这种结构化对象上。
 */
const getLocalStorageJSON = <T>(key: string, fallback: T, validate: (v: unknown) => v is T): T => {
	try {
		const raw = getLocalStorage(key)
		if (typeof raw !== 'string' || raw.length === 0) return fallback
		const parsed: unknown = JSON.parse(raw)
		return validate(parsed) ? parsed : fallback
	} catch {
		return fallback
	}
}
const isSeoConfig = (v: unknown): v is SeoConfig => {
	if (!v || typeof v !== 'object') return false
	const o = v as Record<string, unknown>
	return typeof o.title === 'string' && typeof o.keywords === 'string' && typeof o.description === 'string'
}

/**
 * 从 localStorage 安全读取 number：解析失败 / 越界 / NaN / 抛错都退回 fallback。
 * 用于 siderWidth 这种带上下界的整数。
 */
const getLocalStorageNumber = (key: string, fallback: number, min: number, max: number): number => {
	try {
		const raw = getLocalStorage(key)
		if (typeof raw !== 'string' || raw.length === 0) return fallback
		const n = Number(raw)
		if (!Number.isFinite(n)) return fallback
		return Math.min(max, Math.max(min, Math.round(n)))
	} catch {
		return fallback
	}
}

/**
 * 把任意输入 clamp 到合法的 siderWidth 范围，并按 step 对齐
 * - NaN / 负数 / 0 → fallback
 * - 小于 min 或大于 max → 钳到边界
 * - 按 step 取整（默认 4）
 */
const normalizeSiderWidth = (raw: number): number => {
	if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_SIDER_WIDTH
	const clamped = Math.min(SIDER_WIDTH_MAX, Math.max(SIDER_WIDTH_MIN, raw))
	return Math.round(clamped / SIDER_WIDTH_STEP) * SIDER_WIDTH_STEP
}

/** 读取系统当前深浅偏好。matchMedia 不可用时默认 light。 */
const getSystemPref = (): ResolvedTheme => {
	if (typeof window === 'undefined' || !window.matchMedia) return 'light'
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** 把用户偏好解析成真正生效的主题。 */
const resolveTheme = (t: Theme): ResolvedTheme => (t === 'system' ? getSystemPref() : t)

/** 把已解析主题同步到 body 的 theme-mode 属性（驱动 Semi 的暗色 CSS）。 */
const applyBodyTheme = (resolved: ResolvedTheme) => {
	if (typeof document === 'undefined') return
	if (resolved === 'dark') document.body.setAttribute('theme-mode', 'dark')
	else document.body.removeAttribute('theme-mode')
}

const globalStore = create<GlobalState>((set, get) => {
	// 初始偏好与解析结果（与 index.html 内联脚本必须保持一致）
	const initialTheme = getLocalStorageSafe('plus_one_theme', 'light' as Theme, isTheme)
	const initialResolved = resolveTheme(initialTheme)

	// ---- 系统主题监听器（仅在偏好为 'system' 时挂载） ----
	let mql: MediaQueryList | null = null
	const systemListener = (e: MediaQueryListEvent) => {
		const resolved: ResolvedTheme = e.matches ? 'dark' : 'light'
		applyBodyTheme(resolved)
		set({ resolvedTheme: resolved })
	}
	const ensureSystemListener = (theme: Theme) => {
		if (mql) {
			mql.removeEventListener('change', systemListener)
			mql = null
		}
		if (theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
			mql = window.matchMedia('(prefers-color-scheme: dark)')
			mql.addEventListener('change', systemListener)
		}
	}
	ensureSystemListener(initialTheme)

	// 通用：根据是否有坐标决定是否做涟漪
	const doSwitch = (nextResolved: ResolvedTheme, x?: number, y?: number) => {
		const cx = x ?? (typeof window !== 'undefined' ? window.innerWidth / 2 : 0)
		const cy = y ?? (typeof window !== 'undefined' ? window.innerHeight / 2 : 0)
		if (typeof document !== 'undefined' && document.startViewTransition) {
			document
				.startViewTransition(() => {
					applyBodyTheme(nextResolved)
					set({ resolvedTheme: nextResolved })
				})
				.ready.then(() => {
					const maxSize = Math.hypot(Math.max(cx, innerWidth - cx), Math.max(cy, innerHeight - cy))
					document.documentElement.animate(
						{
							clipPath: [`circle(0px at ${cx}px ${cy}px)`, `circle(${maxSize}px at ${cx}px ${cy}px)`]
						},
						{
							duration: 1000,
							easing: 'ease-in-out',
							pseudoElement: '::view-transition-new(root)'
						}
					)
				})
		} else {
			applyBodyTheme(nextResolved)
			set({ resolvedTheme: nextResolved })
		}
	}

	return {
		locale: getLocalStorageSafe('plus_one_locale', 'zh_CN' as 'zh_CN' | 'en_GB', isLocale),
		loading: false,
		theme: initialTheme,
		resolvedTheme: initialResolved,
		primaryHue: getLocalStorageSafe('plus_one_primary_hue', 'blue' as PrimaryHue, isPrimaryHue),
		containerWidth: getLocalStorageSafe('plus_one_container_width', 'compact' as ContainerWidth, isContainerWidth),
		themeDrawerOpen: false,
		watermarkEnabled: getLocalStorageBoolean('plus_one_watermark_enabled', false),
		systemName: getLocalStorageString('plus_one_system_name', 'Plus-One UI'),
		copyright: getLocalStorageString('plus_one_copyright', '© 2024 Plus-One UI. All rights reserved.'),
		seo: getLocalStorageJSON<SeoConfig>('plus_one_seo', DEFAULT_SEO, isSeoConfig),
		siderWidth: getLocalStorageNumber('plus_one_sider_width', DEFAULT_SIDER_WIDTH, SIDER_WIDTH_MIN, SIDER_WIDTH_MAX),
		toogleLoading: (val = false) => set({ loading: val }),
		changeLocale: (val: 'zh_CN' | 'en_GB') => {
			if (val === 'zh_CN') set({ locale: 'zh_CN' })
			else set({ locale: 'en_GB' })
		},
		switchMode: (clickX?: number, clickY?: number) => {
			// Header 按钮：基于 *已解析* 主题取反，并把偏好也锁到该值（脱离 system）
			const { resolvedTheme } = get()
			const next: ResolvedTheme = resolvedTheme === 'light' ? 'dark' : 'light'
			set({ theme: next, resolvedTheme: next })
			setLocalStorage('plus_one_theme', next)
			ensureSystemListener(next) // 切到非 system 时确保旧监听被卸
			doSwitch(next, clickX, clickY)
		},
		setTheme: (t: Theme, opts?: { x?: number; y?: number }) => {
			const { theme: current } = get()
			if (t === current) return
			const resolved = resolveTheme(t)
			set({ theme: t, resolvedTheme: resolved })
			setLocalStorage('plus_one_theme', t)
			ensureSystemListener(t)
			doSwitch(resolved, opts?.x, opts?.y)
		},
		setPrimaryHue: (h: PrimaryHue) => {
			set({ primaryHue: h })
			setLocalStorage('plus_one_primary_hue', h)
		},
		setContainerWidth: (w: ContainerWidth) => {
			set({ containerWidth: w })
			setLocalStorage('plus_one_container_width', w)
		},
		setThemeDrawerOpen: (open: boolean) => set({ themeDrawerOpen: open }),
		setWatermarkEnabled: (enabled: boolean) => {
			set({ watermarkEnabled: enabled })
			setLocalStorage('plus_one_watermark_enabled', String(enabled))
		},
		setSystemName: (name: string) => {
			// 注意：不能在 setter 里把空字符串回填成默认值 —
			// Input 是受控组件，setter 把空值改成默认会让用户清空输入的瞬间又被回填，
			// 看起来就像「删不掉」。
			// 这里只存原值；显示端（Sider header.text / useDocumentMeta）自己做 .trim() 兜底。
			set({ systemName: name })
			setLocalStorage('plus_one_system_name', name.trim())
		},
		setCopyright: (text: string) => {
			// 同上：不要在 setter 里回填默认。
			set({ copyright: text })
			setLocalStorage('plus_one_copyright', text.trim())
		},
		setSeo: (next: SeoConfig) => {
			set({ seo: next })
			try {
				setLocalStorage('plus_one_seo', JSON.stringify(next))
			} catch {
				/* JSON.stringify 不会对纯字符串对象失败，但稳妥起见吞掉 */
			}
		},
		setSiderWidth: (w: number) => {
			const normalized = normalizeSiderWidth(w)
			set({ siderWidth: normalized })
			setLocalStorage('plus_one_sider_width', String(normalized))
		}
	}
})

const { getState, setState, subscribe, destroy } = globalStore

export { getState, setState, subscribe, destroy }

export default globalStore
